import type React from 'react';
import { useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getUser } from '../api/admin';
import { useAsync } from '../api/useAsync';
import { AsyncBoundary, Avatar, Pill, StatCard, relativeTime } from '../components/ui';
import type { BotDeck, OwnedCard, RecentMatch, UserStats } from '../types/admin';

/**
 * Read-only detail for a real player: identity, gameplay stats, recent matches,
 * cards and decks. Unlike the bot detail page there are no edit actions — real
 * accounts are never mutated from the admin panel (the server enforces that too).
 */
export function UserDetailPage(): React.JSX.Element {
  const { id = '' } = useParams();
  const load = useCallback(() => getUser(id), [id]);
  const { data, loading, error, reload } = useAsync(load, [id]);

  return (
    <div className="page">
      <header className="page__header">
        <div>
          <Link to="/users" className="back-link">
            ← Usuários
          </Link>
          <h1>{data?.user.displayName ?? 'Usuário'}</h1>
          {data ? (
            <p className="page__subtitle">
              @{data.user.userName}
              {data.user.status !== 'active' ? (
                <>
                  {' '}
                  <Pill tone="danger">{data.user.status}</Pill>
                </>
              ) : null}
              {data.user.email ? <> · {data.user.email}</> : null} · entrou{' '}
              {relativeTime(data.user.createdAt)}
            </p>
          ) : null}
        </div>
        {data ? <Avatar src={data.user.profileImgUrl} name={data.user.displayName} size={56} /> : null}
      </header>

      <AsyncBoundary loading={loading && !data} error={error} onRetry={reload}>
        {data ? (
          <>
            {data.degraded.length > 0 ? (
              <div className="banner banner--warn">
                <div>
                  Não foi possível carregar: {data.degraded.join(', ')} — o servidor de
                  inventário não respondeu.
                </div>
                {/* Show the upstream reason: the browser only ever sees a 200
                    here (o BFF degrada de propósito), so sem isto não há como
                    diagnosticar. */}
                {data.degradedReasons
                  ? Object.entries(data.degradedReasons).map(([field, reason]) => (
                      <div key={field} className="banner__detail">
                        <code>
                          {field}: {reason}
                        </code>
                      </div>
                    ))
                  : null}
              </div>
            ) : null}

            <StatsSection stats={data.stats} provisioned={data.provisioned} />

            <RecentMatchesSection matches={data.recentMatches} playerId={id} />

            <UserCardsSection cards={data.cards} />

            <UserDecksSection decks={data.decks} />
          </>
        ) : null}
      </AsyncBoundary>
    </div>
  );
}

// --- Stats ------------------------------------------------------------------

function StatsSection({
  stats,
  provisioned,
}: {
  stats: UserStats | null;
  provisioned: boolean;
}): React.JSX.Element {
  if (!provisioned || !stats) {
    return (
      <section className="panel">
        <h2 className="panel__title">Estatísticas</h2>
        <div className="empty-state">Este jogador ainda não jogou nenhuma partida.</div>
      </section>
    );
  }

  const decided = stats.wins + stats.losses;
  const winRate = decided > 0 ? Math.round((stats.wins / decided) * 100) : null;

  return (
    <section className="panel">
      <h2 className="panel__title">Estatísticas</h2>
      <div className="stat-grid">
        <StatCard label="Vitórias" value={stats.wins} />
        <StatCard label="Derrotas" value={stats.losses} />
        {/* StatCard renders integers only; win rate is a percentage, so render
            the same card markup with a formatted value. */}
        <div className="stat-card">
          <div className="stat-card__label">Taxa de vitória</div>
          <div className="stat-card__value">{winRate === null ? '—' : `${winRate}%`}</div>
        </div>
        <StatCard label="Partidas" value={stats.totalMatches} />
        <StatCard label="Nível" value={stats.level} />
        <StatCard label="XP" value={stats.xp} />
        <StatCard label="Moedas" value={stats.currency} />
        <StatCard label="Sequência de vitórias" value={stats.winStreak} />
        <StatCard label="Sequência de login" value={stats.loginStreak} />
      </div>
    </section>
  );
}

// --- Recent matches ---------------------------------------------------------

/** "3m 12s" from a duration in milliseconds. */
function formatDuration(ms: number): string {
  const totalSeconds = Math.max(0, Math.round(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
}

function RecentMatchesSection({
  matches,
  playerId,
}: {
  matches: RecentMatch[];
  playerId: string;
}): React.JSX.Element {
  return (
    <section className="panel">
      <h2 className="panel__title">Partidas recentes ({matches.length})</h2>
      {matches.length === 0 ? (
        <div className="empty-state">Nenhuma partida recente.</div>
      ) : (
        <div className="data-table" role="table">
          {/* Base .data-table grid is 4 columns — exactly what this table needs,
              so no per-table modifier class. */}
          <div className="data-table__head" role="row">
            <span role="columnheader">Resultado</span>
            <span role="columnheader">Adversário</span>
            <span role="columnheader">Duração</span>
            <span role="columnheader">Quando</span>
          </div>
          {matches.map((match) => {
            const result =
              match.winnerId === null
                ? { label: 'Empate', tone: 'neutral' as const }
                : match.winnerId === playerId
                  ? { label: 'Vitória', tone: 'success' as const }
                  : { label: 'Derrota', tone: 'danger' as const };
            const opponent = match.participantIds.find((p) => p !== playerId) ?? '—';
            return (
              <div className="data-table__row" role="row" key={match.matchId}>
                <span className="cell" role="cell">
                  <Pill tone={result.tone}>{result.label}</Pill>
                </span>
                <span className="cell cell--dim" role="cell" title={opponent}>
                  {opponent === '—' ? '—' : `…${opponent.slice(-6)}`}
                </span>
                <span className="cell" role="cell">
                  {formatDuration(match.durationMs)}
                </span>
                <span className="cell cell--dim" role="cell" title={match.createdAt}>
                  {relativeTime(match.createdAt)}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

// --- Cards (read-only) ------------------------------------------------------

function UserCardsSection({ cards }: { cards: OwnedCard[] }): React.JSX.Element {
  const sorted = [...cards].sort((a, b) =>
    (a.name ?? a.cardId).localeCompare(b.name ?? b.cardId),
  );

  return (
    <section className="panel">
      <h2 className="panel__title">Cartas ({cards.length})</h2>
      <div className="card-grid">
        {sorted.map((card) => (
          <div className="card-tile" key={card.instanceId}>
            {card.artUrl ? (
              <img className="card-tile__art" src={card.artUrl} alt="" loading="lazy" />
            ) : (
              <div className="card-tile__art card-tile__art--empty" />
            )}
            <div className="card-tile__body">
              <div className="card-tile__name">{card.name ?? card.cardId}</div>
              <div className="card-tile__meta">
                {card.cost !== null ? <Pill>custo {card.cost}</Pill> : null}
                {card.missingFromCatalog ? <Pill tone="danger">fora do catálogo</Pill> : null}
                {card.inDeckIds.length > 0 ? <Pill tone="accent">no deck</Pill> : null}
              </div>
            </div>
          </div>
        ))}
        {cards.length === 0 ? (
          <div className="empty-state">Este jogador não tem cartas.</div>
        ) : null}
      </div>
    </section>
  );
}

// --- Decks (read-only) ------------------------------------------------------

function UserDecksSection({ decks }: { decks: BotDeck[] }): React.JSX.Element {
  return (
    <section className="panel">
      <h2 className="panel__title">Decks ({decks.length})</h2>
      {decks.length === 0 ? (
        <div className="empty-state">Este jogador não tem decks.</div>
      ) : (
        decks.map((deck, index) => (
          <div key={deck.deckId} style={index > 0 ? { marginTop: '1.25rem' } : undefined}>
            <h3 className="panel__subtitle">
              {deck.name} {deck.isActive ? <Pill tone="success">ativo</Pill> : null}
            </h3>
            <div className="slot-grid">
              {deck.cards.map((card, index) => (
                <div className="slot slot--filled" key={card.instanceId ?? `slot-${index}`}>
                  {card.artUrl ? (
                    <img className="slot__art" src={card.artUrl} alt="" loading="lazy" />
                  ) : null}
                  <span className="slot__label">{card.name ?? card.cardId ?? index + 1}</span>
                  {card.missing ? <Pill tone="danger">carta ausente</Pill> : null}
                </div>
              ))}
            </div>
          </div>
        ))
      )}
    </section>
  );
}
